import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./contexts/AuthContext";

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const googleButtonRef = useRef(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [appleScriptLoaded, setAppleScriptLoaded] = useState(false);
  const [oneTapInitialized, setOneTapInitialized] = useState(false);

  const BACKEND_URL = window._env_?.REACT_APP_BACKEND_URL;
  const GOOGLE_CLIENT_ID = window._env_?.REACT_APP_GOOGLE_CLIENT_ID;
  const APPLE_CLIENT_ID = window._env_?.REACT_APP_APPLE_CLIENT_ID;
  const FACEBOOK_APP_ID = window._env_?.REACT_APP_FACEBOOK_APP_ID;
  const redirectUri = `${window.location.origin}/auth-callback.html`;

  // Initialize Google Sign-In and OneTap
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || isAuthenticated) return;

    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts && googleButtonRef.current) {
        try {
          // Initialize regular Google Sign-In button
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            width: "100%",
            text: "signin_with",
          });

          setGoogleScriptLoaded(true);

          // Initialize OneTap if user is not authenticated
          if (!isAuthenticated && !oneTapInitialized) {
            initializeOneTap();
          }
        } catch (error) {
          console.error("Error initializing Google Sign-In:", error);
          setGoogleScriptLoaded(false);
        }
      }
    };

    const initializeOneTap = () => {
      try {
        // Configure OneTap
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: true, // Automatically select account if user has only one
          cancel_on_tap_outside: true,
        });

        // Prompt OneTap
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log(
              "OneTap was not displayed:",
              notification.getNotDisplayedReason()
            );
          } else if (notification.isDismissedMoment()) {
            console.log(
              "OneTap was dismissed:",
              notification.getDismissedReason()
            );
          }
        });

        setOneTapInitialized(true);
      } catch (error) {
        console.error("Error initializing OneTap:", error);
      }
    };

    // Check if Google Sign-In script is loaded
    if (window.google && window.google.accounts) {
      initializeGoogleSignIn();
    } else {
      // Wait for the script to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.accounts) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogle);
        if (!googleScriptLoaded) {
          console.warn(
            "Google Sign-In script failed to load within 10 seconds"
          );
        }
      }, 10000);
    }
  }, [GOOGLE_CLIENT_ID, isAuthenticated, oneTapInitialized]);

  // Disable OneTap when user is authenticated
  useEffect(() => {
    if (isAuthenticated && window.google && window.google.accounts) {
      try {
        window.google.accounts.id.cancel();
      } catch (error) {
        console.log("Error canceling OneTap:", error);
      }
    }
  }, [isAuthenticated]);

  // Initialize Apple Sign-In
  useEffect(() => {
    if (!APPLE_CLIENT_ID || isAuthenticated) return;

    const initializeAppleSignIn = () => {
      if (window.AppleID) {
        try {
          // Initialize Apple Sign-In
          // Note: redirectURI must point to backend because Apple uses form_post
          var appleRedirectBaseURI = window._env_?.REACT_IS_DEBUG === "true" ? "https://hydrochloric-cerebrational-claire.ngrok-free.dev" : BACKEND_URL;
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: "name email",
            redirectURI: `${appleRedirectBaseURI}/auth/apple/callback`,
          });
          setAppleScriptLoaded(true);
          
          // Add click handler to store current path before sign-in
          const appleButton = document.getElementById('appleid-signin');
          if (appleButton) {
            appleButton.addEventListener('click', () => {
              const currentPath = window.location.pathname + window.location.search + window.location.hash;
              localStorage.setItem('apple_auth_redirect', currentPath);
            });
          }
        } catch (error) {
          console.error("Error initializing Apple Sign-In:", error);
          setAppleScriptLoaded(false);
        }
      }
    };

    // Check if Apple Sign-In script is loaded
    if (window.AppleID) {
      initializeAppleSignIn();
    } else {
      // Wait for the script to load
      const checkApple = setInterval(() => {
        if (window.AppleID) {
          clearInterval(checkApple);
          initializeAppleSignIn();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkApple);
        if (!appleScriptLoaded) {
          console.warn("Apple Sign-In script failed to load within 10 seconds");
        }
      }, 10000);
    }
  }, [APPLE_CLIENT_ID, BACKEND_URL, isAuthenticated, appleScriptLoaded]);

  const handleGoogleCredentialResponse = async (response) => {
    setLoading(true);
    setError(null);

    try {
      // Send the credential token to your backend
      const backendResponse = await fetch(`${BACKEND_URL}/auth/google/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          credential: response.credential,
        }),
      });

      const data = await backendResponse.json();

      if (backendResponse.ok && data.access_token) {
        login(data.access_token, data.user);
      } else {
        setError(
          "Google authentication failed: " +
            (data.detail || data.message || "Unknown error")
        );
      }
    } catch (error) {
      setError("Google authentication failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    setError(null);

    // Open Google OAuth in popup (fallback for older implementation)
    const popup = window.open(
      `https://accounts.google.com/o/oauth2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${redirectUri}&scope=openid email profile&response_type=code&state=google`,
      "google-login",
      "width=500,height=600"
    );

    // Listen for messages from popup
    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
        login(event.data.token, event.data.user);
        popup.close();
        window.removeEventListener("message", messageListener);
        setLoading(false);
      } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
        setError(
          "Google authentication failed: " +
            (event.data.error || "Unknown error")
        );
        setLoading(false);
        popup.close();
        window.removeEventListener("message", messageListener);
      }
    };

    window.addEventListener("message", messageListener);

    // Handle popup being closed manually
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        setLoading(false);
      }
    }, 1000);
  };

  const handleFacebookLogin = () => {
    setLoading(true);
    setError(null);

    // Open Facebook OAuth in popup
    const popup = window.open(
      `https://www.facebook.com/v18.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&scope=email,public_profile&response_type=code&state=facebook`,
      "facebook-login",
      "width=500,height=600"
    );

    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "FACEBOOK_AUTH_SUCCESS") {
        login(event.data.token, event.data.user);
        popup.close();
        window.removeEventListener("message", messageListener);
        setLoading(false);
      } else if (event.data.type === "FACEBOOK_AUTH_ERROR") {
        setError(
          "Facebook authentication failed: " +
            (event.data.error || "Unknown error")
        );
        setLoading(false);
        popup.close();
        window.removeEventListener("message", messageListener);
      }
    };

    window.addEventListener("message", messageListener);

    // Handle popup being closed manually
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        setLoading(false);
      }
    }, 1000);
  };

  // Don't render if user is already authenticated
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-100 text-red-800 p-3 rounded">{error}</div>
      )}

      <div className="space-y-3">
        {/* Google Sign-In Button */}
        <div className="w-full">
          <div ref={googleButtonRef}></div>
          {/* Fallback button if Google Sign-In doesn't load */}
          {!googleScriptLoaded && (
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded flex items-center justify-center mt-2"
            >
              {loading ? "Logging in..." : "Login with Google"}
            </button>
          )}
        </div>

        {/* Apple Sign-In Button */}
        {APPLE_CLIENT_ID && (
          <div className="w-full">
            {appleScriptLoaded && (
              <div
                id="appleid-signin"
                data-color="black"
                data-border="true"
                data-type="sign in"
                data-height="40"
              ></div>
            )}
          </div>
        )}

        {/*<button
          onClick={handleFacebookLogin}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded flex items-center justify-center"
        >
          {loading ? 'Logging in...' : 'Login with Facebook'}
        </button>*/}
      </div>
    </div>
  );
};

export default Login;
